import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorProgress, OngoingProgress, PauseProgress, ThreeDotsSvg, UnknownProgress, PauseSvg, UnknownSvg, ErrorSvg, AngularLoadingSpinner, InProgressSandGlass } from "../Svgs";
import ShimmerMenuList from "../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu";
import useFiles from "../../../../hooks/useFiles";
import { formatFileSize } from "../../../../../../utils/commonFunctions";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import useUrlParams from "../../../../hooks/useUrlParams";

const UplinkQueue = ({ setTypeOfAction, setActionFileId, isReadOnlyActions = false }) => {
    const { tab_name } = useUrlParams()
    const [openThreeDots, setOpenThreeDots] = useState<any | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [categorizedFiles, setCategorizedFiles] = useState({
        completed: [] as any[],
        uploading: [] as any[],
        error: [] as any[],
        planned: [] as any[],
    });
    const { allACPStorageFiles, isGroundFilesLoading } = useFiles();

    //CLOSE POPUP ON SCROLL
    useEffect(() => {
        const containers = document.querySelectorAll('.queue-planned-rows-body');

        const handleScroll = () => {
            if (openThreeDots) setOpenThreeDots(null);
        };

        containers.forEach(container => {
            container.addEventListener('scroll', handleScroll);
        });

        return () => {
            containers.forEach(container => {
                container.removeEventListener('scroll', handleScroll);
            });
        };
    }, [openThreeDots]);

    // HANDLE THREE DOTS MENU OPENING AND CLOSING
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const threeDotsButtonOngoing = document.querySelectorAll('.right-buttons-container-ongoing');
            const threeDotsButtonPlanned = document.querySelectorAll('.right-buttons-container-planned');
            for (let i = 0; i < threeDotsButtonOngoing.length; i++) {
                if (threeDotsButtonOngoing[i].contains(e.target as Node)) {
                    return; // Clicked inside any of the menus
                }
            }
            for (let i = 0; i < threeDotsButtonPlanned.length; i++) {
                if (threeDotsButtonPlanned[i].contains(e.target as Node)) {
                    return; // Clicked inside any of the menus
                }
            }
            if (!openThreeDots) return; //if already close then return
            const menu = openThreeDots !== null ? menuRefs.current[openThreeDots?.uniqueId] : null;
            const target = e.target as Node;
            if (menu && !menu.contains(target)) {
                setOpenThreeDots(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openThreeDots]);

    //CATEGORISED FILES BASED ON THE TYPE
    useEffect(() => {
        if (!allACPStorageFiles) return;
        const uplinkMap = allACPStorageFiles["uplink-files"];
        if (!uplinkMap) return;

        const filesArray = Array.from(uplinkMap.values());

        const STATUS_TO_CATEGORY: Record<string, keyof typeof updated> = {
            Completed: 'completed',
            Uplinking: 'uploading',
            Resume: 'uploading',
            Unknown: 'uploading',
            Suspended: 'uploading',
            'Suspend Inprogress': 'uploading',
            'Resume Inprogress': 'uploading',
            // Error: 'uploading', // Uncomment when needed
            Pending: 'planned',
        };

        const updated = {
            completed: [] as any[],
            uploading: [] as any[],
            error: [] as any[],
            planned: [] as any[],
        };

        filesArray.forEach((file) => {
            const category = STATUS_TO_CATEGORY[file.fileStatus];
            if (category) {
                updated[category].push(file);
            }
        });

        setCategorizedFiles(updated);
    }, [allACPStorageFiles]);


    //THREE DOTS MENU OPEN AND CLOSE
    const handleThreeDotsMenu = useCallback((e, file: any) => {
        if (isReadOnlyActions) return;
        e.stopPropagation();
        if (file?.fileStatus === 'Suspend Inprogress' || file?.fileStatus === 'Resume Inprogress') {
            setOpenThreeDots(null);
        } else {
            setOpenThreeDots(prev => (prev?.uniqueId === file?.uniqueId ? null : file));
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            if (tab_name === 'files-ground-files') {
                setMenuPosition({ top: rect.bottom + window.scrollY - 100, left: rect.right - 120 + window.scrollX });
            } else {
                setMenuPosition({ top: rect.bottom + window.scrollY - 100 + 70, left: rect.right - 120 + window.scrollX });
            }
        }
    }, [isReadOnlyActions])

    //CANCEL ONGOING UPLOAD TO SATELLITE
    const handleCancel = useCallback((file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId });
        setTypeOfAction('cancel');
    }, []);

    // PAUSE ONGOING UPLOAD TO SATELLITE
    const handlePause = useCallback((file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId });
        setTypeOfAction('pause');
    }, []);

    // RESUME ONGOING UPLOAD TO SATELLITE
    const handleResume = useCallback((file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId });
        setTypeOfAction('resume');
    }, []);

    return (
        <>
            {/* QUEUE OF FILES */}
            {!isGroundFilesLoading &&
                <div className="queue-body">
                    {/* ONGOING SECTION */}
                    {categorizedFiles?.uploading?.length > 0 &&
                        <div className="queue-body-ongoing">
                            <div className={`queue-planned-rows-body`}>
                                {categorizedFiles?.uploading?.map((file) => (
                                    <div className={`queue-body-row ${(file?.fileStatus === 'Uplinking' || file?.fileStatus === 'Suspend Inprogress') ? 'active' : (file?.fileStatus === 'Resume Inprogress' || file?.fileStatus === 'Suspended') ? 'Pause' : file?.fileStatus}`} key={file.uniqueId}>
                                        <div className="queue-body-row-left-section">
                                            <div className="d-flex justify-content-center align-items-center" style={{ width: '15px', height: '15px' }}>
                                                {(file?.fileStatus === 'Uplinking' || file?.fileStatus === 'Suspend Inprogress') && <AngularLoadingSpinner />}
                                                {file?.fileStatus === 'Error' ? <ErrorSvg /> : file?.fileStatus === 'Unknown' ? <UnknownSvg /> : (file?.fileStatus === 'Suspended' || file?.fileStatus === 'Resume Inprogress') ? <PauseSvg /> : ''}
                                            </div>
                                            <div className="name-date-container">
                                                <span className="file-name-in-queue d-block text-truncate" title={file?.fileName}>{file?.fileName}</span>
                                                <span className="file-date-in-queue-ongoing">
                                                    <span className="location-block d-none">Dubai, UAE</span>
                                                    <span className="size-block">{formatFileSize(file?.fileSize)}</span>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="queue-body-row-right-section gap-2">
                                            {(file?.fileStatus === 'Suspend Inprogress' || file?.fileStatus === 'Resume Inprogress') && <InProgressSandGlass />}
                                            <div className='circular__progress__groundfiles'>
                                                {(file?.fileStatus === 'Uplinking' || file?.fileStatus === 'Suspend Inprogress') && <OngoingProgress progress={file?.progress} />}
                                                {file?.fileStatus === 'Error' ? <ErrorProgress progress={file?.progress} /> : file?.fileStatus === 'Unknown' ? <UnknownProgress progress={file?.progress} /> : (file?.fileStatus === 'Suspended' || file?.fileStatus === 'Resume Inprogress') ? <PauseProgress progress={file?.progress} /> : ''}
                                            </div>
                                            <div className={`right-buttons-container-ongoing cursor-pointer ${(file?.fileStatus === 'Suspend Inprogress' || file?.fileStatus === 'Resume Inprogress') ? 'disable' : ''}`} onClick={(e) => { handleThreeDotsMenu(e, file) }}>
                                                {(file?.fileStatus === 'Suspend Inprogress' || file?.fileStatus === 'Resume Inprogress') ? (
                                                    <CustomToolTip
                                                        title={file?.fileStatus === 'Suspend Inprogress' ? 'Pausing File Uplink' : 'Resuming File Uplink'}
                                                        placement='top'
                                                    >
                                                        <ThreeDotsSvg />
                                                    </CustomToolTip>

                                                ) : ((file?.fileStatus !== "Suspended" &&
                                                    file?.fileStatus !== "Error" &&
                                                    file?.fileStatus !== "Uplinking" &&
                                                    file?.fileStatus !== "Suspend Inprogress" &&
                                                    file?.fileStatus !== "Resume Inprogress" &&
                                                    file?.fileStatus !== "Completed" &&
                                                    file?.fileStatus !== "Cancelled" &&
                                                    file?.fileStatus !== "Pending") ||
                                                    (file?.fileStatus === "" ||
                                                        !file?.fileStatus)
                                                ) ? '' : <span className={isReadOnlyActions ? 'opacity-50' : ''}><ThreeDotsSvg /></span>}

                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {/* Floating menu outside rows */}

                                <div
                                    ref={(el) => {
                                        if (openThreeDots) {
                                            menuRefs.current[openThreeDots?.uniqueId] = el;
                                        }
                                    }}
                                    className={`three-dots-menu ${!isReadOnlyActions && openThreeDots?.uniqueId ? "active" : ""}`}
                                    style={{ width: '82px', top: menuPosition.top, left: menuPosition.left }}
                                >

                                    {(openThreeDots?.fileStatus === 'Uplinking') && <span role="button" className='three-dots-menu-options' onClick={() => { handlePause(openThreeDots) }}><span className="ms-2">Pause</span></span>}
                                    {(openThreeDots?.fileStatus === 'Suspended') && <span role="button" className='three-dots-menu-options' onClick={() => { handleResume(openThreeDots) }}><span className="ms-2">Resume</span></span>}
                                    {/* needed in future */}
                                    {/* {(openThreeDots?.fileStatus === 'Error' || openThreeDots?.fileStatus === 'Unknown') && <span role="button" className='three-dots-menu-options' onClick={() => { handleRestart(openThreeDots) }}><span className="ms-2">Restart</span></span>}
                                    {(openThreeDots?.fileStatus === 'Error' || openThreeDots?.fileStatus === 'Unknown') && <span role="button" className='three-dots-menu-options' onClick={() => { handleDismiss(openThreeDots) }}><span className="ms-2">Dismiss</span></span>} */}
                                    {(openThreeDots?.fileStatus === 'Uplinking' || openThreeDots?.fileStatus === 'Suspended') && <span role="button" className='three-dots-menu-options' onClick={() => { handleCancel(openThreeDots) }}><span className="ms-2">Cancel</span></span>}
                                </div>
                            </div>
                        </div>
                    }

                    {/* DIVIDER LINE */}
                    {/* <div style={{ margin: '12px 0', height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.1)', display: 'none' }} /> */}

                    {/* PlANNED SECTION ---> Needed in upcoming development */}
                    {/* <div className="queue-body-planned d-none">
                        <div className="queue-body-queue-headers">
                            <span>planned</span>
                            <span className="header-count">{categorizedFiles?.planned?.length}</span>
                        </div>
                        <div className={`queue-planned-rows-body`}>
                            {categorizedFiles?.planned.map((file) => (
                                <div className="queue-body-row">
                                    <div className="queue-body-row-left-section">
                                        <div className="d-flex justify-content-center align-items-center"><PendingSvg /></div>
                                        <div className="name-date-container">
                                            <span className="file-name-in-queue d-block text-truncate" title={file?.fileName}>{file?.fileName}</span>
                                            <span className="file-date-in-queue d-none">Upload Starts in 36 Hours</span>
                                        </div>
                                    </div>

                                    <div className="queue-body-row-right-section">

                                        <div
                                            className="right-buttons-container-planned cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleThreeDotsMenu(file?.uniqueId);
                                            }}
                                        >
                                            <ThreeDotsSvg />

                                            <div
                                                ref={(el) => menuRefs.current[file?.uniqueId] = el}
                                                className={`three-dots-menu ${openThreeDots === file?.uniqueId ? 'active' : ''}`}
                                                style={{ width: '82px' }}
                                                onClick={() => handleCancel(file)}
                                            >
                                                Cancel
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div> */}
                </div>
            }

            {/* SHIMMER WHEN LOADING */}
            {isGroundFilesLoading && <ShimmerMenuList rowHeight={58} />}
        </>
    );

};

export default UplinkQueue;
